import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { parseISO } from "date-fns";
import { fmtDate, relativeDistance } from "@/lib/dates";
import {
  TrendingUp,
  CheckCircle2,
  Microscope,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, SectionLabel } from "@/components/ui/Card";
import { listReviews, getReview } from "@/api/reviews";
import { cn } from "@/lib/utils";

type FilterKey = "all" | string;

interface FilterMeta {
  id: FilterKey;
  label: string;
  icon: LucideIcon;
}

const FILTERS: FilterMeta[] = [
  { id: "all", label: "all", icon: Sparkles },
  { id: "weekly_review", label: "weekly", icon: TrendingUp },
  { id: "daily_check", label: "daily", icon: CheckCircle2 },
  { id: "test_analysis", label: "tests", icon: Microscope },
  { id: "back_emergency", label: "back_alerts", icon: AlertTriangle },
];

const FILTER_LABEL_KEY: Record<string, string> = {
  all: "reviews.filter_all",
  weekly: "reviews.filter_weekly",
  daily: "reviews.filter_daily",
  tests: "reviews.filter_tests",
  back_alerts: "reviews.filter_back_alerts",
};

const TYPE_ACCENT: Record<string, string> = {
  weekly_review: "border-l-amber",
  daily_check: "border-l-taupe/60",
  test_analysis: "border-l-coral",
  monthly_deep_review: "border-l-navy",
  back_emergency: "border-l-coral-dark",
};

// Implementation detail (Haiku/Sonnet/Opus) is hidden from athletes —
// the Admin → AI Spend tab is where models surface for billing truth.
const AI_BADGE = "AI";

// Strip markdown punctuation from previews so the list-of-cards reads like
// flowing prose, not raw `## Тренировки` headers.
function previewText(s: string | null | undefined, maxLen = 220): string {
  if (!s) return "";
  const cleaned = s
    .replace(/^#+\s+/gm, "")        // drop ## H2 markers
    .replace(/\*\*/g, "")            // drop **bold** markers
    .replace(/`/g, "")               // drop code-fence backticks
    .replace(/\n{2,}/g, "\n")        // collapse blank lines
    .replace(/\n/g, " · ")           // newline → bullet sep
    .trim();
  return cleaned.length > maxLen
    ? cleaned.slice(0, maxLen).trimEnd() + "…"
    : cleaned;
}

export default function Reviews() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get("open");
  const initialExpanded = openParam ? Number(openParam) : null;
  const [expandedId, setExpandedId] = useState<number | null>(
    Number.isFinite(initialExpanded) && initialExpanded && initialExpanded > 0
      ? initialExpanded
      : null,
  );

  // Clear ?open=… once consumed so back/forward navigation works cleanly.
  useEffect(() => {
    if (openParam) {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
    // Intentionally run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listQ = useQuery({
    queryKey: ["reviews", filter],
    queryFn: () =>
      listReviews({
        review_type: filter === "all" ? undefined : filter,
        days: 180,
        limit: 80,
      }),
    staleTime: 30_000,
  });

  const reviews = listQ.data?.reviews ?? [];
  const counts = listQ.data?.type_counts ?? {};
  const totalAll = listQ.data?.total ?? 0;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <SectionLabel>{t("reviews.section_title")}</SectionLabel>
        <h1 className="font-serif text-[26px] font-medium leading-snug md:text-[30px]">
          {t("reviews.h1")}
        </h1>
        <p className="mt-1 text-[13px] text-taupe">{t("reviews.subtitle")}</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const count = f.id === "all" ? totalAll : counts[f.id] ?? 0;
          const isActive = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors",
                isActive
                  ? "border-navy bg-navy text-cream"
                  : "border-cream-border bg-cream-light text-navy-deep hover:bg-cream-border/40",
              )}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
              {t(FILTER_LABEL_KEY[f.label] || "reviews.filter_all")}
              <span
                className={cn(
                  "ml-1 text-[11px]",
                  isActive ? "text-cream-light" : "text-taupe",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {listQ.isLoading ? (
        <div className="py-10 text-center text-taupe">{t("reviews.loading")}</div>
      ) : reviews.length === 0 ? (
        <Card>
          <p className="text-center text-[14px] text-taupe">{t("reviews.empty_state")}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <ReviewCard
              key={r.id}
              review={r}
              expanded={expandedId === r.id}
              onToggle={() =>
                setExpandedId(expandedId === r.id ? null : r.id)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewCard({
  review,
  expanded,
  onToggle,
}: {
  review: import("@/types").ReviewSummary;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const accent = TYPE_ACCENT[review.type] || "border-l-cream-border";
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border-l-4 border-t border-r border-b border-cream-border bg-cream-light shadow-soft",
        accent,
      )}
    >
      <button
        onClick={onToggle}
        className="block w-full p-4 text-left hover:bg-cream-border/20 md:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-[1.5px] text-amber-dark">
                {t(`reviews.type_${review.type}`, { defaultValue: review.type_label })}
              </span>
              {review.week_number != null ? (
                <span className="text-[10px] text-taupe">
                  · {t("reviews.week_n", { n: review.week_number })}
                </span>
              ) : null}
            </div>
            <div className="text-[12px] text-taupe">
              {review.created_at ? (
                <>
                  {fmtDate(review.created_at, "d MMMM yyyy, HH:mm")}
                  {" · "}
                  {relativeDistance(review.created_at)}
                </>
              ) : null}
            </div>
            {review.preview ? (
              <p className="mt-2 line-clamp-3 text-[14px] leading-relaxed text-navy-deep">
                {previewText(review.preview)}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="rounded-full bg-cream px-2 py-0.5 text-[10px] uppercase tracking-wider text-taupe">
              {AI_BADGE}
            </span>
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-taupe" strokeWidth={1.8} />
            ) : (
              <ChevronDown className="h-4 w-4 text-taupe" strokeWidth={1.8} />
            )}
          </div>
        </div>
      </button>

      {expanded ? (
        <div className="border-t border-cream-border bg-cream px-4 py-4 md:px-5 md:py-5">
          <ReviewDetailsInner id={review.id} />
        </div>
      ) : null}
    </div>
  );
}

function ReviewDetailsInner({ id }: { id: number }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["review", id],
    queryFn: () => getReview(id),
  });

  if (q.isLoading) return <div className="text-[13px] text-taupe">{t("reviews.loading")}</div>;
  if (!q.data) return null;

  const d = q.data;
  const resp = d.claude_response as Record<string, unknown>;
  const weeklyPlan = d.type === "weekly_review" && resp ? resp : null;
  const workouts = (weeklyPlan?.workouts as unknown[]) || [];
  const redFlags = (weeklyPlan?.red_flags as unknown[]) || [];
  const adjustments = (weeklyPlan?.adjustments_rationale as string) || "";
  const targetTss = weeklyPlan?.target_weekly_tss as number | undefined;
  const phase = weeklyPlan?.phase as string | undefined;

  // Phase22 — parse structured weekly review into pillar sections.
  // Falls back to single-block render for daily checks / unstructured content.
  const sections = parseSections(d.narrative);
  const isStructured = d.type === "weekly_review" && Object.keys(sections).length >= 3;

  return (
    <div>
      {isStructured ? (
        <StructuredReview sections={sections} />
      ) : (
        <div className="coach-markdown prose-sm max-w-none text-[14px] text-navy-deep">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{d.narrative}</ReactMarkdown>
        </div>
      )}

      {weeklyPlan && (phase || targetTss) ? (
        <div className="mt-4 flex flex-wrap gap-3 border-t border-cream-border pt-3 text-[12px] text-taupe">
          {phase ? (
            <span>
              Фаза:{" "}
              <span className="metric-number text-navy-deep">{phase}</span>
            </span>
          ) : null}
          {targetTss != null ? (
            <span>
              Target TSS:{" "}
              <span className="metric-number text-navy-deep">{targetTss}</span>
            </span>
          ) : null}
          {workouts.length > 0 ? (
            <span>
              Тренировок:{" "}
              <span className="metric-number text-navy-deep">
                {workouts.length}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}

      {adjustments ? (
        <div className="mt-4 rounded-md border border-amber/30 bg-amber/10 p-3 text-[13px]">
          <div className="section-label mb-1">Обоснование корректировок</div>
          <p className="leading-relaxed text-navy-deep">{adjustments}</p>
        </div>
      ) : null}

      {redFlags.length > 0 ? (
        <div className="mt-3 rounded-md border border-coral/30 bg-coral/10 p-3 text-[13px]">
          <div className="section-label mb-1 !text-coral-dark">Red flags</div>
          <ul className="space-y-1 text-navy-deep">
            {redFlags.map((f, i) => (
              <li key={i}>• {String(f)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 flex justify-between border-t border-cream-border pt-3 text-[11px] text-taupe">
        <span>{AI_BADGE}</span>
        <span>{d.type}</span>
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Phase22 — structured weekly review renderer
// ---------------------------------------------------------------------------

function parseSections(md: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!md) return out;
  const lines = md.split("\n");
  let currentKey: string | null = null;
  let buf: string[] = [];
  for (const line of lines) {
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (m) {
      if (currentKey) out[currentKey] = buf.join("\n").trim();
      currentKey = m[1];
      buf = [];
    } else if (currentKey) {
      buf.push(line);
    }
  }
  if (currentKey) out[currentKey] = buf.join("\n").trim();
  return out;
}

function StructuredReview({ sections }: { sections: Record<string, string> }) {
  const summary = sections["Краткий итог"];
  const training = sections["Тренировки"];
  const nutrition = sections["Питание"];
  const recovery = sections["Восстановление"];
  const body = sections["Тело"];
  const plan = sections["План на следующую неделю"];
  const signals = sections["Ключевые сигналы"];

  return (
    <div className="space-y-3">
      {summary ? (
        <div className="rounded-xl border border-amber/40 bg-cream-light p-4">
          <div className="text-[10px] uppercase tracking-wider text-amber-dark">Краткий итог</div>
          <div className="coach-markdown mt-2 text-[15px] leading-relaxed text-navy-deep">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
          </div>
        </div>
      ) : null}

      <PillarCard title="Тренировки" content={training} defaultOpen />
      <PillarCard title="Питание" content={nutrition} />
      <PillarCard title="Восстановление" content={recovery} />
      <PillarCard title="Тело" content={body} />

      {plan ? (
        <div className="rounded-xl border border-amber/40 bg-amber/15 p-4">
          <div className="text-[10px] uppercase tracking-wider text-amber-dark">План на следующую неделю</div>
          <div className="coach-markdown mt-2 text-[14px] leading-relaxed text-navy-deep">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{plan}</ReactMarkdown>
          </div>
        </div>
      ) : null}

      {signals ? (
        <div className="rounded-xl bg-navy p-5 text-cream-light">
          <div className="text-[10px] uppercase tracking-wider text-amber-light">Ключевые сигналы</div>
          <div className="coach-markdown mt-2 text-[14px] leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{signals}</ReactMarkdown>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PillarCard({
  title,
  content,
  defaultOpen = false,
}: {
  title: string;
  content?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (!content) return null;
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="overflow-hidden rounded-xl border border-cream-border bg-cream-light"
    >
      <summary className="cursor-pointer list-none px-4 py-3 hover:bg-cream">
        <span className="text-[14px] font-medium text-navy-deep">{title}</span>
        <span className="float-right text-[12px] text-taupe">{open ? "−" : "+"}</span>
      </summary>
      <div className="coach-markdown px-4 pb-4 text-[13px] leading-relaxed text-navy-deep">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    </details>
  );
}

